import React from 'react';
import { COLORS_DARK, COLORS_LIGHT } from '../components/shared/theme.js';

// ============================================================
// contexts/ThemeContext.js
// EV Powertrain Simulator — 다크/라이트 테마 Context
// 패턴: Context (싱글톤 Context 객체 + 편의 훅)
// 사용법: import { ThemeContext, useTheme } from '../../contexts/ThemeContext.js';
//
// 문제: 모든 탭/shared 파일에서 const COLORS = COLORS_LIGHT 고정
//       → isDark 변경해도 탭 파일 내 COLORS가 반응하지 않음
//
// 해결: ThemeContext로 isDark 공유 → useTheme() 훅으로 COLORS 동적 반환
//
// 사용법:
//   import { ThemeContext, useTheme } from '../../contexts/ThemeContext.js';
//
//   // 컴포넌트 내부에서:
//   const COLORS = useTheme();
//
// Provider는 EVSimulatorInner에서 isDark와 함께 선언:
//   <ThemeContext.Provider value={isDark}>
//     ...
//   </ThemeContext.Provider>
// ============================================================

/**
 * 앱 전역 테마(다크/라이트) 여부를 공유하는 React Context 객체.
 *
 * Provider에서 boolean(isDark)을 value로 전달하며,
 * 기본값 false는 Provider 없이 useContext를 호출할 때의 폴백(라이트모드)이다.
 *
 * @type {React.Context<boolean>}
 */
export const ThemeContext = React.createContext(false); // false = 라이트모드 기본값

/**
 * 현재 테마에 맞는 색상 팔레트 객체를 반환하는 편의 훅.
 *
 * ThemeContext에서 isDark 값을 읽어 COLORS_DARK 또는 COLORS_LIGHT를 반환한다.
 * 컴포넌트는 반환값을 COLORS 변수로 받아 스타일 속성에 직접 사용하면 되며,
 * 테마 전환 시 Context 값이 바뀌면 구독 컴포넌트가 자동으로 리렌더링된다.
 *
 * @returns {typeof COLORS_LIGHT | typeof COLORS_DARK}  현재 테마의 색상 팔레트
 */
export const useTheme = () => {
  const isDark = React.useContext(ThemeContext);
  return isDark ? COLORS_DARK : COLORS_LIGHT;
};